import React, { useContext, useEffect } from 'react';
import {
    ResponseError,
    SelectField,
    InputField,
    AsyncSelectField,
    SubmitButton,
} from '@nintendo/forms';
import { Form, FormikProps } from 'formik';
import { Button, Modal, Alert } from 'react-bootstrap';
import ConfigContext from '../../../contexts/ConfigContext';
import { getUserProjectOrganizations } from '../../../api/amOrganizationApi';
import useAsyncWithState from '../../../hooks/useAsyncWithState';
import { ProjectOrganizationType } from '../../../api/amOrganizationApiTypes';
import { getUserTemplates } from '../../../api/amTemplateApi';
import { AmTemplateType } from '../../../api/amTemplateApiTypes';
import { getAmUsers, getAmUser } from '../../../api/amUserApi';
import { getUserContractSigners } from '../../../api/contractSignerApi';
import { ContractSignerType } from '../../../api/contractSignerApiTypes';
import { INITIAL_SELECT_ID } from '../../../utils/Constants';
import { AmCreateFormType } from './amCreateformTypes';
import { noOptionsHandler } from '../../../utils/displayUtils';
import { AmUserType } from '../../../api/amUserApiTypes';

const CONFIDENTIAL_YES_ID = 1;

export const NO_SIGNATURE_REQUIRED_ID = '0';

export interface AmCreateModalForm {
    id: number;
    projectManagerId: number;
    projectManagerName: string;
    templateType: string;
    templateCode: string;
    confidential: boolean;
    confidentialFlag: number;
    organizationId: number;
    organizationName: string;
    subject: string;
    signatureRequired: string;
    contractOrganizationId?: number | null;
    contractSigner?: number | null;
}

export interface AmCreateFormProps extends FormikProps<AmCreateModalForm> {
    orgId?: number;
    formType: AmCreateFormType;
    responseError: string;
    setResponseError: (errorMessage: string) => void;
    toggleModal: () => void;
}

const AmCreateForm = (props: AmCreateFormProps) => {
    const config = useContext(ConfigContext);
    const {
        values,
        errors,
        touched,
        isSubmitting,
        dirty,
        formType,
        responseError,
        setFieldValue,
        toggleModal,
    } = props;

    const { value: templates } = useAsyncWithState(
        () => getUserTemplates(config),
        [] as AmTemplateType[],
    );
    const orgTemplates = props.orgId
        ? templates.filter((template) => template.orgId === props.orgId)
        : templates;
    const templateTypes = [
        ...Array.from(
            new Map(
                orgTemplates.map((template) => [
                    template.templateType,
                    { name: template.templateType, description: template.templateTypeDescription },
                ]),
            ).values(),
        ),
    ];

    const selectedTemplate = orgTemplates.find((template) => template.code === values.templateCode);

    useEffect(() => {
        if (selectedTemplate && selectedTemplate.confidential) {
            setFieldValue('confidential', true);
            setFieldValue('confidentialFlag', CONFIDENTIAL_YES_ID);
        }
    }, [values.templateCode]);

    const { value: organizations } = useAsyncWithState(
        () => getUserProjectOrganizations(config),
        [] as ProjectOrganizationType[],
    );

    const orgOrganizations =
        selectedTemplate &&
        organizations.filter((organization) => organization.orgId === selectedTemplate.orgId);

    const companyNames =
        orgOrganizations &&
        Array.from(
            new Set(
                orgOrganizations.map((organization) =>
                    organization.name.substring(0, organization.name.indexOf('.')),
                ),
            ),
        );

    const { value: contractSigners } = useAsyncWithState(
        () => getUserContractSigners(config),
        [] as ContractSignerType[],
    );
    const contractOrganizations = Array.from(
        new Map(
            contractSigners.map((contractSigner) => [
                contractSigner.organizationId,
                {
                    id: contractSigner.organizationId,
                    name: contractSigner.organizationName || '',
                },
            ]),
        ).values(),
    );

    let projectManagerValid = true;
    if (values.projectManagerId) {
        const { value: projectManager } = useAsyncWithState(
            () => getAmUser(config, values.projectManagerId),
            null as AmUserType | null,
        );
        projectManagerValid = !projectManager || projectManager.active;
    }

    return (
        <>
            <Modal.Header closeButton>
                <Modal.Title>
                    {formType === AmCreateFormType.CREATE && 'Create Action Memo'}
                    {formType === AmCreateFormType.COPY && 'Copy Action Memo'}
                    {formType === AmCreateFormType.SUPPLEMENT && 'Add Supplement'}
                </Modal.Title>
            </Modal.Header>
            <Form>
                <Modal.Body>
                    <ResponseError errorMessage={responseError} />
                    <Alert
                        show={formType === AmCreateFormType.COPY && !projectManagerValid}
                        variant="warning"
                    >
                        The project manager, <b>{values.projectManagerName}</b>, is no longer an
                        active user.
                    </Alert>
                    <Alert
                        show={formType === AmCreateFormType.SUPPLEMENT && !projectManagerValid}
                        variant="warning"
                    >
                        The project manager, <b>{values.projectManagerName}</b>, is no longer an
                        active user. A project administrator on the action memo must replace the
                        project manager to an active user before you can add a supplement.
                    </Alert>
                    <InputField
                        label="Subject"
                        name="subject"
                        touched={touched.subject}
                        errors={errors.subject}
                        testId="subject"
                        isRequired
                    />
                    <SelectField
                        label="Template Type"
                        name="templateType"
                        options={templateTypes.map((templateType) => ({
                            id: templateType.name,
                            name: templateType.description,
                        }))}
                        touched={touched.templateType}
                        errors={errors.templateType}
                        testId="templateType"
                        isRequired
                        onChange={() => {
                            setFieldValue('templateCode', '');
                        }}
                    />
                    {values.templateType && (
                        <SelectField
                            label="Template"
                            name="templateCode"
                            options={orgTemplates
                                .filter((template) => template.templateType === values.templateType)
                                .map((template) => ({
                                    name: `${template.code} (${template.description})`,
                                    id: template.code,
                                }))}
                            touched={touched.templateCode}
                            errors={errors.templateCode}
                            testId="templateCode"
                            isRequired
                        />
                    )}
                    {orgOrganizations && formType !== AmCreateFormType.SUPPLEMENT && (
                        <AsyncSelectField
                            asyncSelectProps={{
                                cacheOptions: true,
                                loadOptions: async (inputValue: string) => {
                                    if (inputValue.length > 2) {
                                        return orgOrganizations
                                            .filter((organization) =>
                                                organization.name
                                                    .toUpperCase()
                                                    .includes(inputValue.toUpperCase()),
                                            )
                                            .map((organization) => ({
                                                value: organization.id.toString(),
                                                label: organization.name,
                                            }));
                                    }
                                    return [];
                                },
                                noOptionsMessage: noOptionsHandler,
                            }}
                            defaultValue={
                                values.organizationName
                                    ? {
                                          value: values.organizationId.toString(),
                                          label: values.organizationName,
                                      }
                                    : undefined
                            }
                            label="Organization"
                            name="organizationId"
                            touched={touched.organizationId}
                            errors={errors.organizationId}
                            testId="organizationId"
                            tooltip={
                                companyNames &&
                                `To get a list of organizations,<br/>try typing ${
                                    companyNames.length > 1 ? 'one of these' : 'this'
                                } available company name${
                                    companyNames.length > 1 ? 's:<br/>' : ': '
                                }${companyNames.join(', ')}`
                            }
                            isRequired
                        />
                    )}
                    {formType !== AmCreateFormType.SUPPLEMENT && (
                        <AsyncSelectField
                            asyncSelectProps={{
                                cacheOptions: true,
                                loadOptions: async (inputValue: string) => {
                                    if (inputValue.length > 2) {
                                        const amUsers = await getAmUsers(config, {
                                            name: inputValue,
                                        });
                                        if (amUsers) {
                                            return amUsers.map((amUser) => ({
                                                value: amUser.personId.toString(),
                                                label: `${amUser.preferredName} (${amUser.title})`,
                                            }));
                                        }
                                    }
                                    return [];
                                },
                                noOptionsMessage: noOptionsHandler,
                            }}
                            defaultValue={
                                values.projectManagerName
                                    ? {
                                          value: values.projectManagerId.toString(),
                                          label: values.projectManagerName,
                                      }
                                    : undefined
                            }
                            label="Project Manager"
                            name="projectManagerId"
                            touched={touched.projectManagerId}
                            errors={errors.projectManagerId}
                            testId="projectManagerId"
                            isRequired
                        />
                    )}
                    <SelectField
                        label="Confidential"
                        name="confidentialFlag"
                        options={[
                            {
                                id: INITIAL_SELECT_ID,
                                name: 'No',
                            },
                            {
                                id: CONFIDENTIAL_YES_ID,
                                name: 'Yes',
                            },
                        ]}
                        touched={touched.confidentialFlag}
                        errors={errors.confidentialFlag}
                        testId="confidentialFlag"
                        isRequired
                        noDefault
                        onChange={(id: number) => {
                            if (id) {
                                setFieldValue('confidential', id !== INITIAL_SELECT_ID);
                            }
                        }}
                    />
                    <div className="border-top mb-3" />
                    <SelectField
                        label="Is there a legal document that requires a signature?"
                        name="signatureRequired"
                        options={[
                            {
                                id: NO_SIGNATURE_REQUIRED_ID,
                                name: 'No',
                            },
                            {
                                id: 1,
                                name: 'Yes',
                            },
                        ]}
                        touched={touched.signatureRequired}
                        errors={errors.signatureRequired}
                        testId="signatureRequired"
                        isRequired
                        noDefault
                        onChange={() => {
                            setFieldValue('contractOrganizationId', INITIAL_SELECT_ID);
                            setFieldValue('contractSigner', INITIAL_SELECT_ID);
                        }}
                    />
                    {values.signatureRequired !== NO_SIGNATURE_REQUIRED_ID && (
                        <SelectField
                            label="Contract Entity"
                            name="contractOrganizationId"
                            options={contractOrganizations}
                            touched={touched.contractOrganizationId}
                            errors={errors.contractOrganizationId}
                            testId="contractOrganizationId"
                            isRequired
                            tooltip="Which company is party to the agreement?"
                            onChange={() => {
                                setFieldValue('contractSigner', INITIAL_SELECT_ID);
                            }}
                        />
                    )}
                    {values.signatureRequired !== NO_SIGNATURE_REQUIRED_ID &&
                        values.contractOrganizationId &&
                        values.contractOrganizationId > INITIAL_SELECT_ID && (
                            <SelectField
                                label="Contract Signer"
                                name="contractSigner"
                                options={contractSigners
                                    .filter(
                                        (contractSigner) =>
                                            contractSigner.organizationId ===
                                            Number(values.contractOrganizationId),
                                    )
                                    .map((contractSigner) => ({
                                        id: contractSigner.personId,
                                        name: `${contractSigner.personName} (${contractSigner.title})`,
                                    }))}
                                touched={touched.contractSigner}
                                errors={errors.contractSigner}
                                testId="contractSigner"
                                isRequired
                                tooltip="Which officer should sign the attached legal document?"
                            />
                        )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={toggleModal}
                        disabled={isSubmitting}
                    >
                        Close
                    </Button>
                    <SubmitButton
                        className="btn-primary ml-auto"
                        disabled={!dirty}
                        isSubmitting={isSubmitting}
                        buttonText="Create"
                    />
                </Modal.Footer>
            </Form>
        </>
    );
};

export default AmCreateForm;
